import React, { useEffect, useState } from "react";
import axios from "axios";
import { useMessageModal } from "../admin/contexts/MessageModalContext";
import "../styles/marketAnalysisPricing.css";

type MarketAnalysisPlan = {
  id: number;
  title: string;
  amount: number;
  description: string;
};

const API_BASE_URL =
  import.meta.env.VITE_LYRAZONE_BASE_URL || "http://localhost:5000";

const MarketAnalysisPricing: React.FC = () => {
  const { showModal } = useMessageModal();

  const [plans, setPlans] = useState<MarketAnalysisPlan[]>([]);
  const [loading, setLoading] = useState(true);

  const formatMoney = (value: number) => {
    return `RM ${Number(value).toLocaleString("en-MY", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const response = await axios.get(
          `${API_BASE_URL}/market-analysis/plans`,
        );

        if (response.data?.success) {
          setPlans(response.data.data || []);
        }
      } catch (error) {
        console.error("Fetch Market Analysis plans error:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchPlans();
  }, []);

  const redirectToSecurePay = (
    actionUrl: string,
    params: Record<string, string>,
  ) => {
    const form = document.createElement("form");
    form.method = "POST";
    form.action = actionUrl;

    Object.entries(params).forEach(([key, value]) => {
      const input = document.createElement("input");
      input.type = "hidden";
      input.name = key;
      input.value = String(value ?? "");
      form.appendChild(input);
    });

    document.body.appendChild(form);
    form.submit();
  };

  const handleGetAccess = async (plan: MarketAnalysisPlan) => {
    try {
      const storedSubscriberRaw = localStorage.getItem("subscriber_user");

      if (!storedSubscriberRaw) {
        showModal({
          type: "warning",
          title: "Warning",
          message: "You are not logged in, please login first.",
          confirmText: "OK",
        });
        return;
      }

      let subscriberUser: any = null;

      try {
        subscriberUser = JSON.parse(storedSubscriberRaw);
      } catch (error) {
        showModal({
          type: "error",
          title: "Error",
          message: "Invalid subscriber session. Please login again.",
          confirmText: "OK",
        });
        return;
      }

      const buyerName = subscriberUser?.name || "";
      const buyerEmail = subscriberUser?.email || "";
      const buyerPhone =
        subscriberUser?.phoneNo || subscriberUser?.phoneno || "";
      const subscriberId = subscriberUser?.id;

      if (!buyerName || !buyerEmail || !buyerPhone || !subscriberId) {
        showModal({
          type: "warning",
          title: "Warning",
          message:
            "Subscriber information is incomplete. Please login again or update your profile.",
          confirmText: "OK",
        });
        return;
      }

      setLoading(true);

      const response = await fetch(
        `${import.meta.env.VITE_LYRAZONE_BASE_URL}/market-analysis/payments/securepay/create`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            subscriberId,
            planId: plan.id,
            buyerName,
            buyerEmail,
            buyerPhone,
          }),
        },
      );

      const data = await response.json();

      if (!response.ok || !data.success) {
        showModal({
          type: "error",
          title: "Error",
          message:
            data.message || "Unable to initiate Market Analysis payment.",
          confirmText: "OK",
        });
        return;
      }

      if (data?.formAction && data?.securepayPayload) {
        redirectToSecurePay(data.formAction, data.securepayPayload);
      } else {
        showModal({
          type: "error",
          title: "Failed",
          message: "Invalid payment response received.",
          confirmText: "OK",
        });
      }
    } catch (error) {
      console.error("Market Analysis top-up error:", error);

      showModal({
        type: "error",
        title: "Failed",
        message: "Unable to start Market Analysis top-up payment.",
        confirmText: "OK",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="market-analysis-pricing-page">
      <div className="market-analysis-pricing-header">
        <h2>Market Analysis Access</h2>
        <p>
          Top up your account balance and use it whenever you need Market Price
          Analysis. Your funds remain in your subscriber account until spent.
        </p>
      </div>

      {loading ? (
        <div className="market-analysis-pricing-loading">
          Loading pricing plans...
        </div>
      ) : (
        <div className="market-analysis-pricing-grid">
          {plans.map((plan) => (
            <div className="market-analysis-pricing-card" key={plan.id}>
              <h3>{plan.title}</h3>

              <strong>{formatMoney(plan.amount)}</strong>

              <p>{plan.description}</p>

              <ul>
                <li>Funds stay in your account until spent</li>
                <li>Use balance for Market Price Analysis searches</li>
                <li>Suitable for Malaysia product price checking</li>
              </ul>

              <button type="button" onClick={() => handleGetAccess(plan)}>
                Get Access
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MarketAnalysisPricing;
